// packages/frontend/src/features/payment/AgeVerifButton.tsx
import React from 'react';
import { navigateInApp } from '../../utils/telegram';

export type AgeVerifButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Optional URL to the AgeVerif logo (24x24 recommended). */
  iconSrc?: string;
  /** Accessible label for the icon (screen readers). */
  iconAlt?: string;
  /** Make the button stretch to container width. Default: true. */
  fullWidth?: boolean;
  /** Show loading state (disables clicks and shows spinner/text). */
  loading?: boolean;
  /**
   * If no onClick is supplied, the button will navigate here (Telegram-safe).
   * Default: /payment/verify-age (AgeVerif-first flow).
   */
  to?: string;
  /** Session ID used for handoff. If provided, appended as ?sessionId=... */
  sessionId?: string;
  /** Custom loading label. Default: '...' */
  loadingLabel?: string;
};

const DEFAULT_TO = (import.meta as any)?.env?.VITE_AGEVERIF_PATH || '/payment/verify-age';

export const AgeVerifButton: React.FC<AgeVerifButtonProps> = React.memo(
  ({
    iconSrc,
    iconAlt = 'Age verification',
    fullWidth = true,
    loading = false,
    disabled,
    children,
    type = 'button',
    to = DEFAULT_TO,
    sessionId,
    loadingLabel = '...',
    className,
    style: styleProp,
    onClick,
    ...rest
  }) => {
    const [hover, setHover] = React.useState(false);
    const [active, setActive] = React.useState(false);
    const clickedWhileLoading = React.useRef(false);

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      if (onClick) {
        onClick(e);
      } else {
        const target = sessionId ? `${to}?sessionId=${encodeURIComponent(sessionId)}` : to;
        navigateInApp(target);
      }
    };

    // Spinner (SVG), respects prefers-reduced-motion
    const Spinner = React.useMemo(
      () => (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          style={{ marginRight: 8 }}
        >
          <defs>
            <style>
              {`@media (prefers-reduced-motion: reduce) { .spin { animation: none !important; } }
                @keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
          </defs>
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            opacity="0.25"
          />
          <path
            className="spin"
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            style={{ transformOrigin: '12px 12px', animation: 'spin 0.8s linear infinite' }}
          />
        </svg>
      ),
      []
    );

    const style: React.CSSProperties = {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--sp-sm, 8px)',
      width: fullWidth ? '100%' : 'auto',
      maxWidth: 'var(--layout-max, 900px)',
      height: 48,
      minHeight: 48,
      padding: `0 var(--sp-md, 12px)`,
      paddingLeft: iconSrc ? 'calc(var(--sp-md, 12px) + 28px)' : 'var(--sp-md, 12px)',
      fontSize: 'var(--fs-button, 16px)',
      fontFamily: 'var(--font-body, system-ui, -apple-system, Segoe UI, Roboto, sans-serif)',
      color: 'var(--btn-av-fg, currentColor)',
      backgroundColor: hover ? 'var(--btn-av-bg-hover, #f7f7f7)' : 'var(--btn-av-bg, #fff)',
      border: '1px solid rgba(0,0,0,0.15)',
      borderRadius: 'var(--r-md, 10px)',
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled || loading ? 0.6 : 1,
      textAlign: 'center',
      userSelect: 'none',
      transition: 'transform 40ms ease, background-color 120ms ease, box-shadow 120ms ease',
      outline: 'none',
      transform: active && !disabled && !loading ? 'translateY(1px)' : 'none',
      ...styleProp,
    };

    const logoStyle: React.CSSProperties = {
      position: 'absolute',
      left: 'var(--sp-md, 12px)',
      height: 24,
      width: 24,
      objectFit: 'contain',
      pointerEvents: 'none',
    };

    return (
      <button
        type={type}
        disabled={disabled || loading}
        style={style}
        className={className}
        aria-disabled={disabled || loading || undefined}
        aria-busy={loading || undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => {
          setHover(false);
          setActive(false);
        }}
        onMouseDown={() => setActive(true)}
        onMouseUp={() => setActive(false)}
        onClick={(e) => {
          if (loading) {
            // Defensive: block repeated clicks if parent didn't lock.
            if (clickedWhileLoading.current) return;
            clickedWhileLoading.current = true;
            window.setTimeout(() => (clickedWhileLoading.current = false), 600);
            e.preventDefault();
            return;
          }
          handleClick(e);
        }}
        {...rest}
      >
        {iconSrc && <img src={iconSrc} alt={iconAlt} style={logoStyle} />}
        {loading ? (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {Spinner}
            {loadingLabel}
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

AgeVerifButton.displayName = 'AgeVerifButton';
export default AgeVerifButton;
